import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './UserDetails.css';

function UserDetails() {
  const { userId, groupId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [nominees, setNominees] = useState({});
  const [winners, setWinners] = useState({});
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [userId, groupId]);

  const loadData = async () => {
    try {
      const [userPredictions, nomineesData, winnersData] = await Promise.all([
        api.getUserPredictions(userId, groupId),
        api.getNominees(),
        api.getWinners()
      ]);

      if (userPredictions.user) {
        setUser(userPredictions.user);
      } else {
        const userData = await api.getUser(userId);
        setUser(userData);
      }

      setPredictions(userPredictions.predictions || {});
      setScore(userPredictions.score || 0);
      setNominees(nomineesData);
      setWinners(winnersData);
    } catch (err) {
      setError('Failed to load user predictions');
    } finally {
      setLoading(false);
    }
  };

  const getTierForCategory = (category) => {
    if (!nominees[category] || nominees[category].length === 0) return '1';
    return nominees[category][0].tier;
  };

  const isCorrect = (category, prediction) => {
    return winners[category] && winners[category].winner === prediction;
  };

  const getCorrectCount = () => {
    return Object.entries(predictions).filter(([cat, pred]) =>
      isCorrect(cat, pred)
    ).length;
  };

  const totalCategories = Object.keys(nominees).length;
  const predictionsCount = Object.keys(predictions).length;
  const correctCount = getCorrectCount();
  const hasWinners = Object.keys(winners).length > 0;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-details-page">
        <div className="error-message">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="user-details-page">
      <button className="back-button" onClick={() => navigate(-1)}>
        &#8592; Back
      </button>

      <div className="user-header card">
        <div className="user-avatar">
          {user?.profilePic ? (
            <img src={user.profilePic} alt={user.name} />
          ) : (
            user?.name?.charAt(0).toUpperCase()
          )}
        </div>
        <div className="user-info">
          <h1>{user?.name}</h1>
          <div className="user-stats">
            <span>{predictionsCount}/{totalCategories} predictions</span>
            {hasWinners && (
              <>
                <span className="stat-divider">|</span>
                <span>{correctCount} correct ({Math.round((correctCount / predictionsCount) * 100) || 0}%)</span>
                <span className="stat-divider">|</span>
                <span className="score">Score: {score}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="predictions-list">
        <h2>Predictions</h2>
        {/* Sort categories by tier (1, 2, 3) */}
        {Object.keys(nominees)
          .sort((a, b) => {
            const tierA = parseInt(getTierForCategory(a));
            const tierB = parseInt(getTierForCategory(b));
            return tierA - tierB;
          })
          .map(category => {
            const prediction = predictions[category];
            const tier = getTierForCategory(category);
            const correct = prediction && isCorrect(category, prediction);
            const hasWinner = winners[category];

            return (
              <div
                key={category}
                className={`prediction-item tier-${tier} ${hasWinner ? (correct ? 'correct' : 'incorrect') : ''}`}
              >
                <div className="prediction-category">
                  <span className="category-name">{category}</span>
                  <span className={`tier-badge tier-${tier}`}>
                    {tier === '1' ? '1pt' : tier === '2' ? '2pts' : '3pts'}
                  </span>
                </div>
                <div className="prediction-value">
                  {prediction || <span className="no-prediction">No prediction</span>}
                </div>
                {hasWinner && (
                  <div className="prediction-result">
                    {correct ? (
                      <span className="result-correct">&#10003;</span>
                    ) : (
                      <span className="result-incorrect">
                        &#10007; {winners[category].winner}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default UserDetails;
